/**
 * Stock Strength Scanner Dashboard
 * 6-layer multi-factor scoring: RS + Trend + Momentum + MoneyFlow + Resilience - Risk
 *
 * File split:
 *   constants.js        — color maps, metadata, helpers
 *   primitives.jsx      — Card, CardTitle, Tile, ScoreBar, RiskBar, ComponentLabel, BreakoutBarBadge
 *   StyleChip.jsx       — trade-style chip
 *   ActionTable.jsx     — investor action board bucket panel
 *   LeadersSection.jsx  — leaders + persistent leaders + backtest provenance
 *   SectorBreakdown.jsx — sector aggregate table
 *   RankingsTable.jsx   — full ranked table with search
 *   TickerDrawer.jsx    — slide-in ticker detail drawer
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, CircularProgress,
  Button, TextField, InputAdornment, IconButton,
  Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Refresh, ShowChart, Search, History, Calculate,
  Assessment, EmojiEvents, TrendingUp, TrendingFlat, Speed, Shield,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { stockStrengthAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';

import { thSx, tdSx, fmtDec, riskColor } from './constants';
import { Tile } from './primitives';
import ActionTable from './ActionTable';
import LeadersSection from './LeadersSection';
import SectorBreakdown from './SectorBreakdown';
import RankingsTable from './RankingsTable';
import TickerDrawer from './TickerDrawer';

export default function StockStrength() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [leaders, setLeaders] = useState(null);
  const [summary, setSummary] = useState(null);
  const [actionBoard, setActionBoard] = useState(null);
  const [styleFilter, setStyleFilter] = useState('ALL');
  const [sectorNames, setSectorNames] = useState({});
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [tradeDate, setTradeDate] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const leadersExportRef = useRef(null);

  // Ticker detail drawer
  const [lookupTicker, setLookupTicker] = useState('');
  const [drawerTicker, setDrawerTicker] = useState(null);
  const [tickerHistory, setTickerHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const drawerExportRef = useRef(null);
  const drawerBodyRef = useRef(null);
  const [drawerExporting, setDrawerExporting] = useState(false);

  const openHistory = useCallback(async (ticker) => {
    if (!ticker) return;
    const tk = ticker.toUpperCase().trim();
    setDrawerTicker(tk);
    setHistoryLoading(true);
    setTickerHistory(null);
    try {
      const res = await stockStrengthAPI.getTicker(tk, 60);
      setTickerHistory(res.data);
    } catch (e) {
      setTickerHistory({ error: e.response?.data?.detail || e.message });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleExportDrawerImage = useCallback(async () => {
    const wrapper = drawerExportRef.current;
    const body = drawerBodyRef.current;
    if (!wrapper || drawerExporting) return;
    const savedWrapperHeight = wrapper.style.height;
    const savedBodyOverflow = body ? body.style.overflowY : '';
    const savedBodyMaxHeight = body ? body.style.maxHeight : '';
    const savedBodyHeight = body ? body.style.height : '';
    setDrawerExporting(true);
    try {
      wrapper.style.height = 'auto';
      if (body) {
        body.style.overflowY = 'visible';
        body.style.maxHeight = 'none';
        body.style.height = 'auto';
      }
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        windowHeight: wrapper.scrollHeight,
      });
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = canvas.toDataURL('image/png');
      a.download = `${drawerTicker || 'stock'}-strength-${today}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export image failed:', e);
    } finally {
      wrapper.style.height = savedWrapperHeight;
      if (body) {
        body.style.overflowY = savedBodyOverflow;
        body.style.maxHeight = savedBodyMaxHeight;
        body.style.height = savedBodyHeight;
      }
      setDrawerExporting(false);
    }
  }, [drawerTicker, drawerExporting]);

  const handleExportLeadersImage = useCallback(async () => {
    if (!leadersExportRef.current || exportingImage) return;
    try {
      setExportingImage(true);
      const canvas = await html2canvas(leadersExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const dateStr = tradeDate || new Date().toISOString().slice(0, 10);
      link.download = `stock-strength-leaders-${dateStr}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingImage(false);
    }
  }, [exportingImage, tradeDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [scoresRes, leadersRes, summaryRes, actionRes] = await Promise.all([
        stockStrengthAPI.getScores({ limit: 200 }),
        stockStrengthAPI.getLeaders(),
        stockStrengthAPI.getSummary(),
        stockStrengthAPI.getActionBoard(),
      ]);
      setScores(scoresRes.data.scores || []);
      setTotal(scoresRes.data.total || 0);
      setTradeDate(scoresRes.data.trade_date || '');
      setLeaders(leadersRes.data);
      setSummary(summaryRes.data);
      setActionBoard(actionRes.data);
      setSectorNames(summaryRes.data.sector_names || {});
    } catch (err) {
      console.error('Failed to fetch stock strength data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      await stockStrengthAPI.calculate();
      await fetchData();
    } catch (err) {
      console.error('Calculate failed:', err);
    } finally {
      setCalculating(false);
    }
  };

  const filtered = scores.filter(s =>
    !search || s.ticker.toLowerCase().includes(search.toLowerCase())
  );

  const sn = (code) => {
    const m = sectorNames[code];
    return m ? m.vi : (code || '—');
  };

  // ── Loading skeleton ──
  if (loading) {
    const SkeletonTile = () => (
      <Box sx={{
        bgcolor: '#fff', borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        border: '1px solid #eef2f6',
        p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 140,
      }}>
        <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 1.5 }} animation="wave" />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={60} height={12} animation="wave" />
          <Skeleton variant="text" width={40} height={28} animation="wave" sx={{ mt: 0.3 }} />
          <Skeleton variant="text" width={80} height={10} animation="wave" sx={{ mt: 0.2 }} />
        </Box>
      </Box>
    );
    const SkeletonTableRow = ({ cols = 12 }) => (
      <TableRow>
        {Array.from({ length: cols }).map((_, j) => (
          <TableCell key={j} sx={tdSx}>
            <Skeleton variant="text" width={j === 1 ? 42 : j === 2 ? 80 : j === cols - 1 ? 100 : 30} height={16} animation="wave" />
          </TableCell>
        ))}
      </TableRow>
    );
    return (
      <Box sx={{ px: 0, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5 }} animation="wave" />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="text" width={200} height={30} animation="wave" />
                <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 10 }} animation="wave" />
              </Box>
              <Skeleton variant="text" width={260} height={16} animation="wave" />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '4px' }} animation="wave" />
            <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: '4px' }} animation="wave" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonTile key={i} />)}
        </Box>
        {/* Leaders skeleton */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, px: 0, py: 1.5, mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #eef2f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 0.75 }} animation="wave" />
            <Skeleton variant="text" width={280} height={18} animation="wave" />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {Array.from({ length: 12 }).map((_, j) => (
                    <TableCell key={j} sx={thSx}><Skeleton variant="text" width={j === 2 ? 50 : 35} height={14} animation="wave" /></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{Array.from({ length: 10 }).map((_, i) => <SkeletonTableRow key={i} cols={12} />)}</TableBody>
            </Table>
          </TableContainer>
        </Box>
        {/* Sector skeleton */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, px: 0, py: 1.5, mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #eef2f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 0.75 }} animation="wave" />
            <Skeleton variant="text" width={200} height={18} animation="wave" />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j} sx={thSx}><Skeleton variant="text" width={j === 1 ? 80 : 40} height={14} animation="wave" /></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j} sx={tdSx}><Skeleton variant="text" width={j === 1 ? 120 : j === 2 ? 80 : 30} height={16} animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {/* Rankings skeleton */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, px: 0, py: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #eef2f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 0.75 }} animation="wave" />
              <Skeleton variant="text" width={240} height={18} animation="wave" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={160} height={32} sx={{ borderRadius: '4px' }} animation="wave" />
              <Skeleton variant="text" width={40} height={14} animation="wave" />
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {Array.from({ length: 12 }).map((_, j) => (
                    <TableCell key={j} sx={thSx}><Skeleton variant="text" width={j === 2 ? 50 : 35} height={14} animation="wave" /></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{Array.from({ length: 15 }).map((_, i) => <SkeletonTableRow key={i} cols={12} />)}</TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0, py: 2 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 1.5, mb: 3 }}>
        {/* Left: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
          }}>
            <ShowChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3, lineHeight: 1.2 }}>
                {t('stockStrength.title')}
              </Typography>
              {tradeDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 0.9, py: 0.2, borderRadius: 1.5 }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#3b82f6', boxShadow: '0 0 5px #3b82f6' }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569' }}>{tradeDate}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500, mt: 0.2 }}>
              {t('stockStrength.subtitle')}
            </Typography>
          </Box>
        </Box>
        {/* Right: lookup + actions — always one row, wraps as a unit on small screens */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={lookupTicker}
            onChange={(e) => setLookupTicker(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && lookupTicker.trim()) openHistory(lookupTicker.trim());
            }}
            placeholder={t('stockStrength.lookupPlaceholder', 'Tra mã (VD: HPG)')}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
              endAdornment: lookupTicker ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => openHistory(lookupTicker.trim())} sx={{ p: 0.25, color: '#3b82f6' }}>
                    <History sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 180,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 700,
                letterSpacing: '0.04em', bgcolor: '#fff',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
              },
              '& input::placeholder': { fontFamily: 'inherit', fontWeight: 400, letterSpacing: 'normal' },
            }}
          />
          <Button
            variant="contained"
            startIcon={calculating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Calculate />}
            onClick={handleCalculate}
            disabled={calculating}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {calculating ? t('stockStrength.calculating') : t('stockStrength.calculateNow')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {t('stockStrength.refresh')}
          </Button>
        </Box>
      </Box>

      {/* ── Summary Tiles ── */}
      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1.25, mb: 2 }}>
          <Tile label={t('stockStrength.tiles.totalStocks')} value={summary.total_scored} Icon={Assessment} color="#64748b" />
          <Tile label={t('stockStrength.tiles.leaders')} value={summary.leader_count} Icon={EmojiEvents} color="#10b981"
                sub={t('stockStrength.tiles.persistent', { count: summary.persistent_leader_count })} />
          <Tile label={t('stockStrength.tiles.emerging')} value={summary.emerging_count || 0} Icon={TrendingUp} color="#3b82f6"
                sub={t('stockStrength.tiles.nearLeader')} />
          <Tile label={t('stockStrength.tiles.watch')} value={summary.watch_count || 0} Icon={TrendingFlat} color="#f59e0b"
                sub={t('stockStrength.tiles.monitoring')} />
          <Tile label={t('stockStrength.tiles.avgStrength')} value={fmtDec(summary.avg_strength, 1)} Icon={Speed} color="#3b82f6"
                sub={t('stockStrength.tiles.median', { value: fmtDec(summary.median_strength, 1) })} />
          <Tile label={t('stockStrength.tiles.avgRisk')} value={fmtDec(summary.avg_risk, 1)} Icon={Shield}
                color={riskColor(summary.avg_risk_level_key)}
                sub={t('stockStrength.tiles.median', { value: fmtDec(summary.median_risk, 1) })} />
        </Box>
      )}

      {/* ── Action Board ── */}
      {actionBoard && (
        <Box sx={{ mb: 3 }}>
          <ActionTable bucket="buy"    items={actionBoard.buy_now}         onClickTicker={openHistory} sn={sn} tFn={t} />
          <ActionTable bucket="watch"  items={actionBoard.watch_closely}   onClickTicker={openHistory} sn={sn} tFn={t} />
          <ActionTable bucket="review" items={actionBoard.review_holdings} onClickTicker={openHistory} sn={sn} tFn={t} />
        </Box>
      )}

      {/* ── Leaders & Emerging ── */}
      <LeadersSection
        leaders={leaders}
        openHistory={openHistory}
        exportingImage={exportingImage}
        handleExportLeadersImage={handleExportLeadersImage}
        leadersExportRef={leadersExportRef}
        styleFilter={styleFilter}
        setStyleFilter={setStyleFilter}
        sn={sn}
        t={t}
        actionBoard={actionBoard}
      />

      {/* ── Sector Breakdown ── */}
      <SectorBreakdown summary={summary} sn={sn} t={t} />

      {/* ── Full Rankings ── */}
      <RankingsTable
        filtered={filtered}
        total={total}
        search={search}
        setSearch={setSearch}
        openHistory={openHistory}
        sn={sn}
        t={t}
      />

      {/* ── Ticker Detail Drawer ── */}
      <TickerDrawer
        drawerTicker={drawerTicker}
        setDrawerTicker={setDrawerTicker}
        tickerHistory={tickerHistory}
        historyLoading={historyLoading}
        drawerExporting={drawerExporting}
        drawerExportRef={drawerExportRef}
        drawerBodyRef={drawerBodyRef}
        handleExportDrawerImage={handleExportDrawerImage}
        total={total}
        sn={sn}
        t={t}
      />
    </Box>
  );
}
